/**
 * Normaliza um telefone brasileiro para só dígitos com DDI 55.
 * Aceita "(27) 99999-0000", "27999990000", "+55 27 99999-0000".
 * Devolve null quando não dá para ter certeza do número — inclusive celular incompleto:
 * desde 2016 todo celular tem 9 dígitos (começa com 9); fixo tem 8 e começa com 2 a 5.
 * Sem essa regra, "(27) 9111-1222" (celular faltando 1 dígito) passaria por fixo válido.
 */
export function normalizarTelefoneBR(entrada: string): string | null {
  let local = entrada.replace(/\D/g, '');
  if ((local.length === 12 || local.length === 13) && local.startsWith('55'))
    local = local.slice(2);
  if (local.length !== 10 && local.length !== 11) return null;

  const ddd = local.slice(0, 2);
  const numero = local.slice(2);
  if (ddd.startsWith('0') || ddd.endsWith('0')) return null;
  const celular = numero.length === 9 && numero.startsWith('9');
  const fixo = numero.length === 8 && /^[2-5]/.test(numero);
  return celular || fixo ? `55${local}` : null;
}

/**
 * Celular brasileiro (9 dígitos começando com 9 depois do DDD): é o número que tem WhatsApp.
 * Fixo, incompleto ou vazio dá false.
 */
export function ehCelular(telefone: string | null | undefined): boolean {
  const numero = telefone ? normalizarTelefoneBR(telefone) : null;
  // Normalizado: 55 + DDD + 9 dígitos = 13 (o fixo tem 12).
  return numero !== null && numero.length === 13;
}

/** Como falar com um número: pelo WhatsApp (celular) ou por ligação (fixo). */
export type LinkDeContato =
  | { tipo: 'whatsapp'; url: string; telefone: string }
  | { tipo: 'ligar'; url: string; telefone: string };

/**
 * "Falar com a loja": celular abre o WhatsApp pelo wa.me; fixo vira ligação (tel:). Sem número
 * válido devolve null, e a tela esconde a linha. `telefone` vem normalizado (só dígitos, com DDI).
 */
export function linkDeContato(
  telefone: string | null | undefined,
  mensagem?: string,
): LinkDeContato | null {
  const numero = telefone ? normalizarTelefoneBR(telefone) : null;
  if (!numero) return null;
  return ehCelular(numero)
    ? { tipo: 'whatsapp', url: urlWhatsApp(numero, mensagem), telefone: numero }
    : { tipo: 'ligar', url: `tel:+${numero}`, telefone: numero };
}

/** "5527999990000" → "(27) 99999-0000". Número fora do padrão volta como veio. */
export function formatarTelefoneBR(telefone: string): string {
  const local = telefone.startsWith('55') ? telefone.slice(2) : telefone;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return telefone;
}

/** Link universal (abre o app ou o WhatsApp Web) com a mensagem pronta. */
export function urlWhatsApp(telefone: string, mensagem?: string): string {
  const base = `https://wa.me/${telefone}`;
  return mensagem ? `${base}?text=${encodeURIComponent(mensagem)}` : base;
}

/**
 * Esquema do app instalado. No celular do entregador é melhor que o wa.me:
 * se o WhatsApp não estiver instalado, a abertura falha e o app consegue avisar.
 */
export function urlWhatsAppNativo(telefone: string, mensagem?: string): string {
  const texto = mensagem ? `&text=${encodeURIComponent(mensagem)}` : '';
  return `whatsapp://send?phone=${telefone}${texto}`;
}
